import { ChevronRight, ChevronLeft, User, Folder } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/use-translations';
import { cn } from '@/lib/utils';

import defaultConfig from '../config/bottom-navigation-config.json';
import { BottomNavigationProps } from '../types';

/**
 * BottomNavigation Component
 *
 * A responsive navigation bar for masterclass slides with dual layouts:
 * - Desktop: Full navigation with slide numbers, home button, and next/finish button
 * - Mobile: Compact navigation with progress dots, prev/next buttons, and action icons
 *
 * Features:
 * - Slide accessibility control based on progression
 * - Progress indicators (dots on mobile, numbers on desktop)
 * - Home and patient info quick actions
 * - Document counter badge
 * - Responsive design with fixed positioning on mobile
 * - Customizable via configuration
 *
 * @example
 * ```tsx
 * <BottomNavigation
 *   currentSlide={5}
 *   totalSlides={13}
 *   onSlideChange={(index) => setSlide(index)}
 *   onNext={handleNext}
 *   onPrevious={handlePrev}
 *   onHomeClick={() => navigate('/home')}
 *   visitedSlides={[0, 1, 2, 3, 4, 5]}
 *   maxVisitedSlide={5}
 *   isNextDisabled={!hasAnswered}
 * />
 * ```
 */
export const BottomNavigation = forwardRef<HTMLDivElement, BottomNavigationProps>(
  (
    {
      currentSlide,
      totalSlides,
      onSlideChange,
      onNext,
      onPrevious,
      onHomeClick,
      visitedSlideIds = [],
      currentSlideId = '',
      slides = [],
      isNextDisabled = false,
      isLastSlide = false,
      onAnamneseClick,
      onDocsClick,
      docsCount = 0,
      isSlideAccessible,
      isAccreditationUser = false,
      isCaseCompleted = false,
    },
    ref
  ) => {
    const desktopRef = useRef<HTMLDivElement>(null);
    const mobileRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslations();

    const isMobile = useIsMobile();

    useImperativeHandle(ref, () => {
      return isMobile ? mobileRef.current! : desktopRef.current!;
    }, [isMobile]);

    const getNextButtonText = (isDesktop: boolean) => {
      if (isLastSlide) {
        return t('common.buttons.finish');
      }
      if (isAccreditationUser && isCaseCompleted) {
        return isDesktop ? t('common.buttons.skipSlide') : t('common.buttons.skip');
      }
      return isDesktop ? t('common.buttons.nextSlide') : t('common.buttons.next');
    };

    return (
      <>
        {/* Desktop Navigation */}
        <nav
          ref={desktopRef}
          className="hidden border-t border-border/30 bg-[hsl(230,20%,95%)] px-6 py-3 md:fixed md:bottom-0 md:left-0 md:right-0 md:z-10 md:flex"
        >
          <div className="flex w-full items-center justify-between">
            {/* Left side - Home and User icons */}
            {defaultConfig.navigation.showHomeButton && (
              <div className="flex items-center gap-2">
                <button
                  onClick={onHomeClick}
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-livelinx-purple200 transition-colors hover:bg-livelinx-purple200/5"
                  aria-label={t('common.aria.home') || defaultConfig.accessibility.ariaLabels.home}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </button>
                {defaultConfig.navigation.showUserButton && onAnamneseClick && (
                  <button
                    onClick={onAnamneseClick}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-livelinx-purple200 transition-colors hover:bg-livelinx-purple200/5"
                    aria-label={
                      t('common.aria.patientInformation') ||
                      defaultConfig.accessibility.ariaLabels.patient
                    }
                  >
                    <User className="h-5 w-5" />
                  </button>
                )}
                {onDocsClick && (
                  <div className="relative">
                    <button
                      onClick={onDocsClick}
                      disabled={docsCount === 0}
                      className="flex h-10 w-10 items-center justify-center rounded-lg text-livelinx-purple200 transition-colors hover:bg-livelinx-purple200/5 disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label={
                        t('common.aria.supportingDocuments') ||
                        defaultConfig.accessibility.ariaLabels.documents
                      }
                    >
                      <Folder className="h-5 w-5" />
                    </button>
                    {docsCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-livelinx-red text-[10px] font-bold text-white">
                        {docsCount}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Center - Slide Numbers */}
            {defaultConfig.navigation.showSlideNumbers && (
              <div className="flex items-center gap-2">
                {Array.from({ length: totalSlides - 1 }, (_, i) => i + 1).map((slideNumber) => {
                  const slideId = slides[slideNumber]?.id;
                  const accessible = isSlideAccessible[slideId];
                  const isCurrent = currentSlide === slideNumber;
                  return (
                    <button
                      key={slideNumber}
                      onClick={() => accessible && onSlideChange(slideNumber)}
                      disabled={!accessible}
                      className={cn(
                        'flex h-10 min-w-[40px] items-center justify-center rounded-lg px-3 text-sm font-medium transition-all',
                        {
                          'bg-livelinx-purple200 text-white shadow-md': isCurrent,
                          'cursor-pointer text-muted-foreground hover:bg-muted/50':
                            !isCurrent && accessible,
                          'cursor-not-allowed text-muted-foreground/30': !isCurrent && !accessible,
                        }
                      )}
                    >
                      {slideNumber}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Right - Next/Finish Button */}
            <div className="flex justify-end">
              <Button
                onClick={onNext}
                disabled={isNextDisabled}
                className={cn(
                  'gap-2 shadow-md disabled:cursor-not-allowed disabled:opacity-50',
                  isLastSlide
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-accent text-accent-foreground hover:bg-accent/90'
                )}
              >
                {getNextButtonText(true)}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <nav
          ref={mobileRef}
          className="fixed bottom-0 left-0 right-0 z-10 border-t border-border/30 bg-[hsl(230,20%,95%)] pb-[env(safe-area-inset-bottom,0px)] md:hidden"
          style={{
            position: 'fixed',
            bottom: 0,
            transform: 'translate3d(0, 0, 0)',
            WebkitTransform: 'translate3d(0, 0, 0)',
            willChange: 'transform',
          }}
        >
          {/* Progress indicator */}
          {defaultConfig.mobile.showProgressIndicator && (
            <div className="px-4 pb-2 pt-3">
              <div className="mb-2 flex items-center justify-center gap-1.5">
                {Array.from({ length: totalSlides - 1 }, (_, i) => i + 1).map((slideNumber) => {
                  const slideId = slides[slideNumber]?.id;
                  const accessible = isSlideAccessible[slideId];
                  const isCurrent = currentSlide === slideNumber;
                  return (
                    <button
                      key={slideNumber}
                      onClick={() => accessible && onSlideChange(slideNumber)}
                      disabled={!accessible}
                      className={cn('h-2 rounded-full transition-all', {
                        'w-8 bg-primary': isCurrent,
                        'w-2 bg-muted-foreground/30': !isCurrent && accessible,
                        'w-2 bg-muted-foreground/10': !isCurrent && !accessible,
                      })}
                      aria-label={
                        t('common.aria.slideNavigation', {
                          number: slideNumber,
                        }) ||
                        defaultConfig.accessibility.ariaLabels.slideNavigation.replace(
                          '{number}',
                          String(slideNumber)
                        )
                      }
                    />
                  );
                })}
              </div>
              <div className="text-center text-xs text-muted-foreground">
                {currentSlide === 0 ? t('common.labels.intro') : currentSlide} / {totalSlides - 1}
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-2 px-4 pb-3">
            {/* Home */}
            <button
              onClick={onHomeClick}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-livelinx-purple200 transition-colors hover:bg-livelinx-purple200/5"
              aria-label={t('common.aria.home') || defaultConfig.accessibility.ariaLabels.home}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </button>

            {/* Patient Info - Mobile only */}
            {defaultConfig.mobile.showAnamneseButton && onAnamneseClick && (
              <button
                onClick={onAnamneseClick}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-livelinx-purple200 transition-colors hover:bg-livelinx-purple200/5 md:hidden"
                aria-label={
                  t('common.aria.patientInformation') ||
                  defaultConfig.accessibility.ariaLabels.patient
                }
              >
                <User className="h-5 w-5" />
              </button>
            )}

            {/* Documents - Mobile only */}
            {defaultConfig.mobile.showDocsButton && onDocsClick && (
              <div className="relative md:hidden">
                <button
                  onClick={onDocsClick}
                  disabled={docsCount === 0}
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-livelinx-purple200 transition-colors hover:bg-livelinx-purple200/5 disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label={
                    t('common.aria.supportingDocuments') ||
                    defaultConfig.accessibility.ariaLabels.documents
                  }
                >
                  <Folder className="h-5 w-5" />
                </button>
                {docsCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-livelinx-red text-[10px] font-bold text-white">
                    {docsCount}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-1 items-center justify-center gap-2">
              <Button
                onClick={onPrevious}
                disabled={currentSlide === 0}
                className="flex-1 gap-2 bg-muted text-foreground shadow-sm hover:bg-muted/80 disabled:opacity-50"
                size="sm"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.buttons.prev')}
              </Button>
              <Button
                onClick={onNext}
                disabled={isNextDisabled}
                className={cn(
                  'flex-1 gap-2 shadow-md disabled:cursor-not-allowed disabled:opacity-50',
                  isLastSlide
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-accent text-accent-foreground hover:bg-accent/90'
                )}
                size="sm"
              >
                {getNextButtonText(false)}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </nav>
      </>
    );
  }
);

BottomNavigation.displayName = 'BottomNavigation';
